/**
 * voice_note extension handler.
 *
 * One annotation per recording. The whole note (audio + cursor / viewport
 * track + metadata) is ONE `VoiceNoteFileV1` CBOR document stored under
 * field_key='voice_note_cbor'; the API layer materializes any *_cbor blob into
 * annotation_files (data/projects/<pid>/cases/<cid>/annotations/<id>/voice_note_cbor.cbor),
 * so that file is both the storage and the export format. Scalar summary rows
 * (title, caption, duration_ms, started_at, mime_type, pointer_sample_count)
 * stay in annotation_data so lists and CSV export never decode the blob.
 *
 * This module is ambient (like annotation_time): it is never the active
 * module, has no completion state and is not part of the session history.
 */
import { BaseExtensionHandler } from "../_base.ts"
import type { ExtensionManifest, SerializedAnnotation, StoredAnnotation } from "../_interface.ts"
import { decodeVoiceNoteFile, type VoiceNoteFileV1 } from "./file.ts"
import { createVoiceNoteSchema, updateVoiceNoteSchema, voiceNoteConfigSchema } from "./schema.ts"

export const VOICE_NOTE_FILE_TYPE = "voice_note_cbor" as const

/** API shape returned by `deserializeFromDb` (and the case-scoped list route). */
export interface VoiceNoteApiItem {
  id: string
  submission_id: string
  project_module_id: string
  series_id: string | null
  title: string | null
  caption: string | null
  duration_ms: number
  started_at: string | null
  mime_type: string | null
  pointer_sample_count: number
  /** Relative API path of the CBOR file (`/annotations/:id/files/voice_note_cbor`). */
  file_url: string | null
  file_size_bytes: number | null
  created_at: string
  updated_at: string
}

export class VoiceNoteHandler extends BaseExtensionHandler {
  readonly type = "voice_note"
  readonly manifest: ExtensionManifest = {
    type: "voice_note",
    label: "Voice Notes",
    scope: "series",
    storage: "both",
    supportsLinking: false,
    caseReadable: true
  }

  getConfigSchema() {
    return voiceNoteConfigSchema
  }

  getCreateSchema() {
    return createVoiceNoteSchema
  }

  getUpdateSchema() {
    return updateVoiceNoteSchema
  }

  serializeForDb(apiData: unknown): SerializedAnnotation {
    const data = apiData as { file: Uint8Array; note?: VoiceNoteFileV1 }
    const note = data.note ?? decodeVoiceNoteFile(data.file)
    if (!note) {
      throw new Error("voice_note: file is not a valid voice note (v1) CBOR document")
    }

    return {
      annotation: {
        label_name: note.title,
        label_value: null
      },
      data: [
        { field_key: "title", value_text: note.title },
        { field_key: "caption", value_text: note.caption },
        { field_key: "duration_ms", value_int: note.durationMs },
        { field_key: "started_at", value_text: note.startedAt },
        { field_key: "mime_type", value_text: note.mimeType },
        { field_key: "pointer_sample_count", value_int: note.pointer.t.length },
        { field_key: VOICE_NOTE_FILE_TYPE, value_blob: data.file }
      ],
      files: []
    }
  }

  deserializeFromDb(stored: StoredAnnotation): VoiceNoteApiItem {
    const text = (key: string) => stored.data.find((r) => r.field_key === key)?.value_text ?? null
    const int = (key: string) => stored.data.find((r) => r.field_key === key)?.value_int ?? null
    const file = stored.files.find((f) => f.file_type === VOICE_NOTE_FILE_TYPE)

    return {
      id: stored.annotation.id,
      submission_id: stored.annotation.submission_id,
      project_module_id: stored.annotation.project_module_id,
      series_id: stored.annotation.series_id,
      title: text("title") ?? stored.annotation.label_name,
      caption: text("caption"),
      duration_ms: int("duration_ms") ?? 0,
      started_at: text("started_at"),
      mime_type: text("mime_type"),
      pointer_sample_count: int("pointer_sample_count") ?? 0,
      file_url: file ? `/annotations/${stored.annotation.id}/files/${VOICE_NOTE_FILE_TYPE}` : null,
      file_size_bytes: file?.size_bytes ?? null,
      created_at: stored.annotation.created_at,
      updated_at: stored.annotation.updated_at
    }
  }

  getExportColumns(_templateSchema: unknown): string[] {
    return ["voice_note_count", "voice_note_total_seconds", "voice_note_file_paths"]
  }

  toExportRow(annotations: StoredAnnotation[], _templateSchema: unknown): Record<string, string | number> {
    let totalMs = 0
    const paths: string[] = []
    for (const a of annotations) {
      totalMs += a.data.find((r) => r.field_key === "duration_ms")?.value_int ?? 0
      const file = a.files.find((f) => f.file_type === VOICE_NOTE_FILE_TYPE)
      if (file) {
        const title = a.data.find((r) => r.field_key === "title")?.value_text ?? a.annotation.label_name
        paths.push(title ? `${title}:${file.storage_path}` : file.storage_path)
      }
    }
    return {
      voice_note_count: annotations.length,
      voice_note_total_seconds: Math.round(totalMs / 1000),
      voice_note_file_paths: paths.join("; ")
    }
  }
}
